// =============================================================================
// iTravel 2.0 — useSyncEngine Hook
// =============================================================================
// Manages the sync engine lifecycle in tandem with authentication state.
//
// Behavior:
//   - When the user becomes authenticated → open that account's offline DB and
//     start the sync engine
//   - When the user logs out → stop the engine and close the database, leaving
//     the account's cache and unsent changes on disk
//   - When a different account signs in → close the first one's database and
//     open the second's; neither can see the other's data
//   - On unmount (app close) → clean up listeners
//
// Logging out deliberately does NOT wipe anything. It used to call clearAll(),
// which runs `DELETE FROM sync_queue` — so signing out after a week offline
// destroyed every change that had not reached the server yet, silently.
//
// This hook should be used ONCE at the app root level (e.g., in AppProviders
// or AuthenticatedLayout) — it is NOT meant to be called from multiple places.
//
// Usage:
//   function AuthenticatedLayout() {
//     useSyncEngine();          // ← that's it
//     return <Outlet />;
//   }
// =============================================================================

import { useEffect, useRef } from 'react';

import type { TripListResponse } from '@/features/trips/api/trips.api';
import { HOME_TRIPS_PARAMS, tripKeys } from '@/features/trips/hooks/useTrips';
import { queryClient } from '@/lib/api/query-client';
import { offlineDb } from '@/lib/db/offline-db';
import { hydrateQueryCacheFromOffline } from '@/lib/sync/hydrate-query-cache';
import { prefetchTripsForOffline } from '@/lib/sync/prefetch-trips';
import { useAuthStore } from '@/store/auth.store';
import { useSyncStore } from '@/store/sync.store';

/**
 * Pulls the trip list, then fills in the itineraries behind it.
 *
 * The list is what the home screen draws, so it is fetched first and awaited;
 * everything after it runs in the background, two at a time, ordered by how
 * likely the trip is to be needed. Aborted the moment the session ends.
 */
async function fillOfflineCache(signal: AbortSignal): Promise<void> {
  try {
    const { tripsApi } = await import('@/features/trips/api/trips.api');

    // Under the home screen's own key, and with its page size. Two reasons:
    // the screen's query is then already warm rather than being fetched twice,
    // and one page covers every trip an account realistically has — which is
    // what makes the answer authoritative enough to prune against below.
    const list = await queryClient.fetchQuery<TripListResponse>({
      queryKey: tripKeys.list(HOME_TRIPS_PARAMS),
      queryFn: () => tripsApi.getTrips(HOME_TRIPS_PARAMS),
      staleTime: 0,
    });
    if (signal.aborted) return;

    // The server has just said what this account can see. Anything cached
    // beyond that is a trip the user has left or been removed from, and it
    // must not survive to be hydrated onto the home screen next launch.
    //
    // Only from a complete list: pruning against page one of several would
    // delete trips that are merely on page two.
    if ((list.meta?.totalPages ?? 1) <= 1) {
      try {
        const dropped = await offlineDb.pruneCachedTripsExcept(list.trips.map((trip) => trip.id));
        if (dropped > 0) {
          console.info(`[Offline] Dropped ${dropped} trip(s) this account no longer has access to`);
        }
      } catch (error) {
        console.warn('[Offline] Could not prune the trip cache:', error);
      }
    }
    if (signal.aborted) return;

    const result = await prefetchTripsForOffline(queryClient, list.trips, { signal });
    if (result.fetched > 0) {
      console.info(
        `[Offline] Stored ${result.fetched} itinerar${result.fetched === 1 ? 'y' : 'ies'} ` +
          `(${result.skipped} already current, ${result.failed} unreachable)`,
      );
    }
  } catch {
    // Offline, or the list failed. Nothing to do — the next launch tries again
    // and the screens fall back to whatever is already on disk.
  }
}

/**
 * Opens the push channel. Entirely optional — if it never connects the engine's
 * thirty-second poll behaves exactly as it did before, which is why nothing
 * here is allowed to throw into the caller.
 */
async function startRealtime(): Promise<void> {
  try {
    const [{ connectRealtime }, { useAuthStore }] = await Promise.all([
      import('@/lib/sync/realtime'),
      import('@/store/auth.store'),
    ]);
    // A getter, not the token: it is refreshed silently every fifteen minutes.
    await connectRealtime(() => useAuthStore.getState().accessToken);
  } catch {
    // Polling covers it.
  }
}

async function stopRealtime(): Promise<void> {
  try {
    const { disconnectRealtime } = await import('@/lib/sync/realtime');
    disconnectRealtime();
  } catch {
    // Never opened.
  }
}

/**
 * Tells the server this device belongs to the account that just signed in.
 *
 * Runs after initialization rather than alongside it because it needs a bearer
 * token, and because the OS permission prompt should not appear over a loading
 * screen. Optional in exactly the way the socket is — without it the app still
 * receives everything while it is open.
 */
async function startPush(): Promise<void> {
  try {
    const { claimDeviceForPush } = await import('@/lib/push/push');
    await claimDeviceForPush();
  } catch {
    // No Firebase configuration in this build.
  }
}

async function stopPush(): Promise<void> {
  try {
    const { releaseDeviceFromPush } = await import('@/lib/push/push');
    await releaseDeviceFromPush();
  } catch {
    // Never claimed.
  }
}

/** Best-effort: dropping cached image URLs must never block signing out. */
async function releasePhotoUrls(): Promise<void> {
  try {
    const { releaseAllDisplayUrls } = await import('@/lib/photos/photo-file-store');
    releaseAllDisplayUrls();
  } catch {
    // Nothing cached yet.
  }
}

/**
 * Initializes the offline sync engine when the user is authenticated and
 * tears it down when the user logs out. Also handles cache-clearing on logout.
 *
 * Must be called exactly once inside an authenticated layout/provider.
 */
export function useSyncEngine(): void {
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const userId = useAuthStore((s) => s.user?.id);

  const initialize = useSyncStore((s) => s.initialize);
  const teardown = useSyncStore((s) => s.teardown);
  const isInitialized = useSyncStore((s) => s.isInitialized);
  const isInitializing = useSyncStore((s) => s.isInitializing);

  // Track the user ID that was used for initialization so we can detect
  // user switches (e.g., logout → login as different user) and re-initialize.
  const initializedForUser = useRef<string | null>(null);

  // Stops the background fill on logout, on an account switch, and on unmount —
  // a half-finished prefetch must not write into the next session.
  const prefetchAbort = useRef<AbortController | null>(null);

  const startOfflineFill = () => {
    prefetchAbort.current?.abort();
    const controller = new AbortController();
    prefetchAbort.current = controller;
    // Deliberately not awaited: the caller's job is done once the cache is
    // seeded, and this runs for as long as it takes.
    void fillOfflineCache(controller.signal);
  };

  // -------------------------------------------------------------------------
  // Initialize when authenticated
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    // Already initialized for this user — skip
    if (isInitialized && initializedForUser.current === userId) return;

    // Already initializing — skip
    if (isInitializing) return;

    // Initialized for a DIFFERENT account: close that one's database and open
    // this one's. The first account's data stays exactly where it was.
    if (isInitialized && initializedForUser.current !== userId) {
      (async () => {
        await teardown();
        // Before anything belonging to the new account is loaded: the in-memory
        // query cache still holds the previous one's trips, and the database
        // swap alone does not touch it.
        //
        // reset rather than clear. clear() detaches whatever is already on
        // screen from its query — if a fetch was in flight it never resolves
        // and never retries, which left the home screen on its skeleton for
        // good. reset drops the data and brings the screen back to life.
        await queryClient.resetQueries();
        // Blob URLs minted for the previous account's photos stay valid until
        // they are revoked. The files themselves stay put — that account's
        // unsent photos are still waiting for it to sign back in.
        await releasePhotoUrls();
        await stopRealtime();
        // The device still belongs to the account that just left. Claiming it
        // below moves it, but this closes the window in between.
        await stopPush();
        initializedForUser.current = null;

        await initialize(userId);
        await hydrateQueryCacheFromOffline(queryClient);
        initializedForUser.current = userId;
        startOfflineFill();
        void startRealtime();
        void startPush();
      })();
      return;
    }

    // Normal first-time initialization
    (async () => {
      await initialize(userId);
      // Seed the query cache from disk before anything renders a spinner.
      await hydrateQueryCacheFromOffline(queryClient);
      initializedForUser.current = userId;
      startOfflineFill();
      void startRealtime();
      void startPush();
    })();
  }, [isAuthenticated, userId, isInitialized, isInitializing, initialize, teardown]);

  // -------------------------------------------------------------------------
  // Tear down when the user logs out
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated && isInitialized) {
      (async () => {
        prefetchAbort.current?.abort();
        // Stop and close only. Whatever has not reached the server is still
        // in this account's outbox, waiting for the next sign-in.
        await teardown();
        // The in-memory cache must not outlive the session — it is the one
        // place another account could still see the previous one's trips.
        await queryClient.resetQueries();
        await releasePhotoUrls();
        // The socket is authenticated as the account that just left.
        await stopRealtime();
        // Normally already done by the sign-out itself, while the session was
        // still valid. This covers the paths that skip it — an expired refresh
        // token forcing a sign-out — where the server call cannot succeed but
        // the local listener and claim still have to go.
        await stopPush();
        initializedForUser.current = null;
      })();
    }
  }, [isAuthenticated, isInitialized, teardown]);

  // -------------------------------------------------------------------------
  // Cleanup on unmount (safety net — e.g., if the entire app is unmounted)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const abort = prefetchAbort;
    return () => {
      abort.current?.abort();
      // We intentionally do NOT await here — best-effort cleanup on unmount
      const { isInitialized: stillInitialized } = useSyncStore.getState();
      if (stillInitialized) {
        useSyncStore
          .getState()
          .teardown()
          .catch(() => {
            // Swallow errors during unmount cleanup
          });
      }
    };
  }, []);
}

/**
 * Convenience hook to read the current sync state reactively.
 * Use this in components that need to display sync status (e.g., indicators).
 *
 * Usage:
 *   const { isOnline, pendingCount, isSyncing } = useSyncState();
 */
export function useSyncState() {
  const syncState = useSyncStore((s) => s.syncState);
  const isInitialized = useSyncStore((s) => s.isInitialized);

  return {
    ...syncState,
    isOnline: syncState.network.isOnline,
    connectionType: syncState.network.connectionType,
    isInitialized,
  };
}

/**
 * Convenience hook to get sync actions (manual sync, queue mutation, etc.).
 *
 * Usage:
 *   const { syncNow, queueMutation } = useSyncActions();
 */
export function useSyncActions() {
  const syncNow = useSyncStore((s) => s.syncNow);
  const queueMutation = useSyncStore((s) => s.queueMutation);
  const clearAll = useSyncStore((s) => s.clearAll);

  return { syncNow, queueMutation, clearAll };
}

export default useSyncEngine;
